/**
 * URL query param that carries a shared-search preset id (see `useShareSearchLink`
 * / `useApplySharedSearch`). It is NOT a filter: it is read once on load, applied
 * to the params store, then stripped — so it must be excluded from the filter
 * URL <-> store sync in every place the sync reads the query string.
 */
export const SHARE_SEARCH_URL_PARAM = 'search-id';

/**
 * URL query param that carries a saved-search preset id (see `useApplySavedSearch`).
 * Like `search-id`, it is NOT a filter: it is read once on load, the referenced
 * preset is fetched and applied to the params store, then it is stripped. Keeping
 * only a short preset id in the URL (instead of the full filter set) is what lets
 * "View Search" open in a new tab without risking a 414 (URL too long).
 */
export const SAVED_SEARCH_URL_PARAM = 'saved-search';

/**
 * URL query param that carries an export id (see `useApplyExportSearch`). Like
 * `search-id` / `saved-search`, it is NOT a filter: on load the referenced export
 * is fetched, the original search that produced it (`metadata.params`, falling
 * back to the legacy `metadata.url`) is applied to the params store, then it is
 * stripped. Carrying only the short export id — instead of the full filter set the
 * old "Search" column put in the URL — is what lets "View Search" open without
 * risking a 414 (URL too long) for searches with many filters.
 */
export const EXPORT_SEARCH_URL_PARAM = 'export-search';

/** Query param keys that are not filters and must never enter the params store. */
const NON_FILTER_URL_PARAMS: readonly string[] = [
  SHARE_SEARCH_URL_PARAM,
  SAVED_SEARCH_URL_PARAM,
  EXPORT_SEARCH_URL_PARAM,
];

/**
 * Strip non-filter query params (e.g. `search-id`) so the filter sync never
 * treats them as filter values, ships them to the search API, or echoes them
 * back to the URL. Returns the same reference when nothing was removed.
 */
export function omitNonFilterParams<T extends Record<string, string>>(
  params: T
): T {
  let hasNonFilter = false;
  for (const key of NON_FILTER_URL_PARAMS) {
    if (key in params) {
      hasNonFilter = true;
      break;
    }
  }
  if (!hasNonFilter) return params;

  const clone = { ...params } as Record<string, string>;
  for (const key of NON_FILTER_URL_PARAMS) delete clone[key];
  return clone as T;
}
